import { Box, Flex, Image, HStack, Icon, Text } from '@chakra-ui/react';
import { useMemo } from 'react';
import { useSetRecoilState } from 'recoil';
import { globalVideoPlayerOpenStatus, videoResource } from '../../atoms/rootAtom'; // ← 调整路径
import { AiOutlineFilePdf, AiFillYoutube } from 'react-icons/ai';
import { SiBilibili } from 'react-icons/si';


type VideoItem = { title: string; source: string };
type MenuItem = { name: string; img: string; video: VideoItem };

type CompanyInfoTabsProps = {
  openPreview: (url: string) => void
}

const CompanyInfoTabs = ({ openPreview }: CompanyInfoTabsProps) => {
  // —— 全局视频播放器：打开/设置资源 ——
  const showVideo = useSetRecoilState<boolean>(globalVideoPlayerOpenStatus);
  const setVideo = useSetRecoilState(videoResource);

  const openVideoPlayer = (video: VideoItem) => {
    showVideo(true);
    setVideo(video);
  };

  // —— 配置每个卡片的图片和视频链接 ——
  const menuItems: MenuItem[] = [
    {
      name: 'Setup an outline',
      img: '/static/img1.jpg',
      video: { title: 'Outline Setup', source: 'https://public-librum-files.s3.us-east-1.amazonaws.com/edit/en/02.+Setup+an+Outline.mp4' },
    },
    {
      name: 'Basic Typesetting',
      img: '/static/img2.jpg',
      video: { title: 'Typesetting Basics', source: 'https://public-librum-files.s3.us-east-1.amazonaws.com/edit/en/03.+Basic+Typesetting.mp4' },
    },
    {
      name: 'AI Assisted Content Editing',
      img: '/static/img3.jpg',
      video: { title: 'AI Editing', source: 'https://public-librum-files.s3.us-east-1.amazonaws.com/edit/en/04.+AI+Assisted+Content+Editing.mp4' },
    },
    {
      name: 'Figures and Tables',
      img: '/static/img4.jpg',
      video: { title: 'Figures & Tables', source: 'https://public-librum-files.s3.us-east-1.amazonaws.com/edit/en/05.+Figures+and+Tables.mp4' },
    },
    {
      name: 'Inserting Math',
      img: '/static/img5.jpg',
      video: { title: 'Math Input', source: 'https://public-librum-files.s3.us-east-1.amazonaws.com/edit/en/06.+Inserting+Math.mp4' },
    },
    {
      name: 'Xref and Citation',
      img: '/static/img6.jpg',
      video: { title: 'Xref & Cite', source: 'https://public-librum-files.s3.us-east-1.amazonaws.com/edit/en/07.+Xref+and+Citation.mp4' },
    },
    {
      name: 'Latex Snippets',
      img: '/static/img7.jpg',
      video: { title: 'LaTeX Snippets', source: 'https://public-librum-files.s3.us-east-1.amazonaws.com/edit/en/08.+LaTeX+Snippets.mp4' },
    },
    {
      name: 'Export-Preview-Download',
      img: '/static/img8.jpg',
      video: { title: 'Export / Preview / Download', source: 'https://public-librum-files.s3.us-east-1.amazonaws.com/edit/en/09.+Export-Preview-Download.mp4' },
    },
    {
      name: 'Version History and File Organization',
      img: '/static/img9.jpg',
      video: { title: 'Version History and File Organization', source: 'https://public-librum-files.s3.us-east-1.amazonaws.com/edit/en/10.+Version+History+and+File+Organization.mp4' },
    },
  ];

  // 文档列表（右列）
  const pdfDocs = useMemo(() => ([
    {
      name: 'A Quick Guide to Typesetting Math',
      url: `/static/pdfs/${encodeURIComponent('A Quick Guide to Typesetting Math.pdf')}`,
    },
    {
      name: 'Essentials of Mathematical Writing',
      url: `/static/pdfs/${encodeURIComponent('Essentials of Mathematical Writing.pdf')}`,
    },
  ]), []);

  // Official Channels
  const officialChannels = useMemo(() => ([
    {
      name: 'Doenba Edit Tutorial',
      url: 'https://youtube.com/@DoenbaEditTutorial',
      icon: AiFillYoutube,
      color: '#FF0000',
    },
    {
      name: 'Mathematical Modeling with AI at Doenba Lab',
      url: 'https://www.youtube.com/@ModelingwithAILab',
      icon: AiFillYoutube,
      color: '#FF0000',
    },
    {
      name: 'Mathematical Modeling with AI at Doenba Lab',
      url: 'https://space.bilibili.com/1077873496',
      icon: SiBilibili,
      color: '#00A1D6',
    }
  ]), []);

  return (
    <Flex flexDir={'row'} mt={'10px'} w={'full'} h={'calc(100% - 1px)'} overflow={'hidden'}>
      {/* Column 1: Tutorial Videos */}
      <Flex flex={'1'}  direction="column" overflow="hidden">
        <Text fontSize="18px" fontWeight="700" mb={4} textAlign="center" flexShrink={0}>Tutorial Videos</Text>
        <Box flex="1" overflowY="auto" pr={3}>
          <Flex flexWrap="wrap" justifyContent="flex-start" gap="16px">
            {menuItems.map((item, idx) => (
              <Box key={item.name || idx} width="100%" mb={2}>
                <Image
                  w="100%"
                  src={item.img}
                  alt={item.name || 'video'}
                  cursor="pointer"
                  onClick={() => openVideoPlayer(item.video)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openVideoPlayer(item.video);
                    }
                  }}
                />
                <Box fontSize="16px" alignItems="center" textAlign="center" mt={2}>
                  {item.name}
                </Box>
              </Box>
            ))}
          </Flex>
        </Box>
      </Flex>

      {/* Column 2: Documents */}
      <Flex flex={'1'} px={3} direction="column" overflow="hidden" >
        <Text fontSize="18px" fontWeight="700" mb={4} textAlign="center" flexShrink={0}>Tutorial Documents</Text>
        <Box flex="1" overflowY="auto" borderRight="1px solid #E2E8F0">
          {pdfDocs.map((doc) => (
            <HStack
              key={doc.url}
              width={'100%'}
              spacing={3}
              py={2}
              px={2}
              mb={2}
              borderRadius={'8px'}
              cursor={'pointer'}
              onClick={() => openPreview(doc.url)}
              _hover={{ bg: '#F7FAFC' }}
            >
              <Icon as={AiOutlineFilePdf} boxSize={6} color={'#E53E3E'} />
              <Text mb={0} fontSize={'16px'} flex={'1 1 auto'} noOfLines={1}>{doc.name}</Text>
            </HStack>
          ))}
        </Box>
      </Flex>

      {/* Column 3: Official Channels */}
      <Flex flex={'1'} pl={3} direction="column" overflow="hidden" >
        <Text fontSize="18px" fontWeight="700" mb={4} textAlign="center" flexShrink={0}>Official Channels</Text>
        <Box flex="1" overflowY="auto" >
          {officialChannels.map((channel) => (
            <HStack
              key={channel.url}
              width={'100%'}
              spacing={3}
              py={2}
              px={2}
              mb={2}
              borderRadius={'8px'}
              cursor={'pointer'}
              onClick={() => window.open(channel.url, '_blank')}
              _hover={{ bg: '#F7FAFC' }}
            >
              <Icon as={channel.icon} boxSize={6} color={channel.color} />
              <Text mb={0} fontSize={'16px'} flex={'1 1 auto'}>{channel.name}</Text>
            </HStack>
          ))}
        </Box>
      </Flex>

    </Flex>
  );
};

export default CompanyInfoTabs;
